// Web MIDI API wrapper.
// Handles device enumeration, connection, and event parsing.

import type { MidiEvent } from "./types"

export type MidiEventCallback = (event: MidiEvent) => void

export class MidiManager {
  private access: MIDIAccess | null = null
  private activeInput: MIDIInput | null = null
  private listeners: MidiEventCallback[] = []

  async requestAccess(): Promise<boolean> {
    if (!navigator.requestMIDIAccess) {
      console.warn("Web MIDI API not supported in this browser")
      return false
    }

    try {
      this.access = await navigator.requestMIDIAccess({ sysex: false })
      return true
    } catch (err) {
      console.error("MIDI access denied:", err)
      return false
    }
  }

  listInputs(): MIDIInput[] {
    if (!this.access) return []
    return Array.from(this.access.inputs.values())
  }

  listOutputs(): MIDIOutput[] {
    if (!this.access) return []
    return Array.from(this.access.outputs.values())
  }

  connect(input: MIDIInput): void {
    this.disconnect()
    this.activeInput = input
    input.onmidimessage = (event: MIDIMessageEvent) => {
      const parsed = this.parseMessage(event)
      if (parsed) {
        for (const listener of this.listeners) {
          listener(parsed)
        }
      }
    }
  }

  disconnect(): void {
    if (this.activeInput) {
      this.activeInput.onmidimessage = null
      this.activeInput = null
    }
  }

  onEvent(callback: MidiEventCallback): () => void {
    this.listeners.push(callback)
    return () => {
      this.listeners = this.listeners.filter((l) => l !== callback)
    }
  }

  onStateChange(callback: (event: Event) => void): void {
    if (this.access) {
      this.access.onstatechange = callback
    }
  }

  get connected(): boolean {
    return this.activeInput !== null
  }

  get connectedDevice(): MIDIInput | null {
    return this.activeInput
  }

  private parseMessage(event: MIDIMessageEvent): MidiEvent | null {
    const data = event.data
    if (!data || data.length < 3) return null

    const status = data[0]
    const command = status & 0xf0
    const channel = (status & 0x0f) + 1 // 1-indexed

    // Note On (0x90) or Note Off (0x80)
    if (command === 0x90 || command === 0x80) {
      return {
        timestamp: event.timeStamp,
        channel,
        note: data[1],
        velocity: data[2],
        isNoteOn: command === 0x90 && data[2] > 0,
      }
    }

    return null
  }
}
